import { ParsedListing } from '@bb/domain/dist/types';
import React, {
  createContext,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  useContext,
} from 'react';
import { useHistory } from 'react-router-dom';
import { Modal, Text, message } from 'components';
import { makeModalResponsive } from 'utils';
import { useDebounce } from 'hooks';
import errorReporter from '@app/errorReporter';
import type { UnregisterCallback } from 'history';
import { toggleUseAppointment } from 'api/AppointmentDeskController';
import {
  getListingsForAppointmentDesk,
  getEnabledListingCountForAppointmentDesk,
} from 'api/ListingController';
import { AntdModal } from '@brokerbay/design-components';
import { ModalType, ModalMapType } from './types';
import AppointmentDeskContext, {
  AppointmentDeskStep,
} from '../AppointmentDeskContext';
import FormContext from '../../UserViewPage/FormContext';

type QueryParameterType = {
  searchTerm: string;
  filter: {
    location?: string[];
    availability?: string[];
    useAppointmentDesk?: boolean[];
  };
  pageNumber: number;
  sortData: {
    field: string;
    order: string;
  };
};

type SearchResultType = {
  count: number;
  listings: ParsedListing[];
};

type ManageListingsContextType = {
  // states
  searchResults: SearchResultType;
  loading: boolean;
  queryParameter: QueryParameterType;
  totalCount: number;
  enabledCount: number;
  // update states
  setSearchResults: (searchResults: SearchResultType) => void;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setQueryParameter: React.Dispatch<React.SetStateAction<QueryParameterType>>;
  setEnabledListingsCount: React.Dispatch<React.SetStateAction<number>>;
  // refs
  containerRef: React.MutableRefObject<HTMLDivElement>;
  footerRef: React.MutableRefObject<HTMLDivElement>;
  // functions
  openModal: (type: ModalType) => void;
  sort: (field: string) => void;
};

const initialQueryData: any = {
  searchTerm: '',
  filter: {},
  pageNumber: 0,
  sortData: {
    field: '',
    order: undefined,
  },
};

const ManageListingsContext = createContext<ManageListingsContextType>(null);

export const ManageListingsContextProvider: React.FC = ({ children }) => {
  const [searchResults, setSearchResults] = React.useState<SearchResultType>({
    count: 0,
    listings: [],
  });
  const [loading, setLoading] = React.useState(false);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [queryParameter, setQueryParameter] =
    React.useState<QueryParameterType>(initialQueryData);
  const [totalCount, setTotalCount] = React.useState(0);
  const [enabledCount, setEnabledListingsCount] = React.useState(0);

  const { subscriptionType, userId, currentStep } = useContext(
    AppointmentDeskContext,
  );
  const { showWarningRef } = useContext(FormContext);

  const containerRef = useRef<HTMLDivElement>();
  const footerRef = useRef<HTMLDivElement>();
  const unBlock = useRef<UnregisterCallback>();
  const pathName = useRef<string>();
  const firstRun = useRef(true);

  const debouncedSearchTerm = useDebounce(queryParameter.searchTerm, 500);
  const history = useHistory();

  const sort = (field: string) => {
    setQueryParameter(prev => {
      const { sortData } = prev;
      const order =
        field === sortData.field
          ? {
              ascend: 'descend',
              descend: null,
              null: 'ascend',
            }[sortData.order]
          : 'ascend';
      return {
        ...prev,
        sortData: { field: order ? field : '', order },
      };
    });
  };

  const runSearch = async () => {
    try {
      setLoading(true);

      const getFormattedSortData = () => {
        const { field, order } = queryParameter.sortData;
        const sort_field = field.toString();

        return {
          sort_field,
          sort_asc: (order === 'ascend').toString() as 'true' | 'false',
        };
      };

      const newSearchParameter = {
        ...getFormattedSortData(),
        subscriptionType,
        userId,
        address: queryParameter.searchTerm,
        pageNumber: queryParameter.pageNumber,
        locations: queryParameter.filter.location?.map(location =>
          location.toString(),
        ),
        availability: queryParameter.filter.availability,
        useAppointmentDesk: queryParameter.filter.useAppointmentDesk,
      };

      let listings: ParsedListing[];
      let count: number;
      if (firstRun.current) {
        const [searchResult, enabledListingCount] = await Promise.all([
          getListingsForAppointmentDesk(newSearchParameter),
          getEnabledListingCountForAppointmentDesk(subscriptionType),
        ]);
        ({ count, listings } = searchResult);
        firstRun.current = false;
        setTotalCount(count);
        setEnabledListingsCount(enabledListingCount.count);
      } else {
        ({ count, listings } =
          await getListingsForAppointmentDesk(newSearchParameter));
      }

      setSearchResults(prevResults => ({
        ...prevResults,
        listings,
        count,
      }));
    } catch (error) {
      message.error('Error searching listings');
      errorReporter.error('Error searching listing', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    runSearch();
  }, [
    debouncedSearchTerm,
    queryParameter.pageNumber,
    queryParameter.filter,
    queryParameter.sortData,
  ]);

  const toggleUseAppointmentForListings = useCallback(
    async (toggleOn: boolean) => {
      try {
        setLoading(true);
        const body = {
          subscriptionType,
          toggleOn,
        };
        await toggleUseAppointment(body);

        if (toggleOn) setEnabledListingsCount(totalCount);
        else setEnabledListingsCount(0);

        setSearchResults(prev => ({
          ...prev,
          listings: prev.listings.map(listing => ({
            ...listing,
            useAppointmentDesk: toggleOn,
          })),
        }));
      } catch (error) {
        message.error(`Error ${toggleOn ? 'enabling' : 'disabling'} listings`);
        errorReporter.error(
          `Error ${toggleOn ? 'enabling' : 'disabling'} listings`,
          error,
        );
      } finally {
        setLoading(false);
      }
    },
    [totalCount],
  );

  const modalTypeMap: ModalMapType = useMemo(
    // TODO: onOk functions should be replaced in ENG-8234 and ENG-8235
    () => ({
      leaveThePage: {
        title: (
          <span data-cy="leave-page-modal-title">
            Are you sure you want to leave?
          </span>
        ),
        content: (
          <span data-cy="leave-page-modal-text">
            Your live concierge service has not been activated. Are you sure you
            want to leave this page?
          </span>
        ),
        onOk: () => {
          unBlock.current();
          history.replace(pathName.current);
        },

        onCancel: () => {
          setIsModalOpen(false);
          history.replace(history.location.pathname);
        },
      },
      enableListings: {
        title:
          'Are you sure you want to enable all listings to the Live Concierge Service?',
        onOk: () => {
          toggleUseAppointmentForListings(true);
        },
      },
      disableListings: {
        title:
          'Are you sure you want to disable all listings from the Live Concierge Service?',
        onOk: () => {
          toggleUseAppointmentForListings(false);
        },
      },
    }),
    [history, toggleUseAppointmentForListings],
  );

  const openModal = useCallback(
    (type: ModalType) => {
      const { title, content, onOk, onCancel } = modalTypeMap[type];
      setIsModalOpen(true);
      makeModalResponsive(
        // Note: we are using AntdModal directly here because we have a functionality in
        // common Modal component that whenever the route changes, it closes all open modals
        // which in this case causing a flickering issue.
        AntdModal.confirm({
          title,
          okText: 'Yes',
          cancelText: 'Cancel',
          onOk,
          centered: true,
          ...(onCancel && { onCancel }),
          ...(content && {
            content: <Text type="secondary">{content}</Text>,
          }),
        }),
        {
          dataCys: {
            ok: `${title}-modal-confirm`,
            cancel: `${title}-modal-cancel`,
            content: `${title}-modal-content`,
          },
        },
      );
    },
    [modalTypeMap],
  );

  useEffect(() => {
    if (currentStep === AppointmentDeskStep.Confirmation) {
      return;
    }
    if (showWarningRef) showWarningRef.current = true;
    unBlock.current = history.block(({ pathname }) => {
      if (pathname === history.location.pathname) {
        return;
      }
      pathName.current = pathname;
      // if modal is already opened then avoid opening multiple modals when clicking on browser back button
      if (!isModalOpen) {
        openModal('leaveThePage');
      }
      return false;
    });

    const preventRefresh = (e: BeforeUnloadEvent) => {
      e.returnValue =
        'Your live concierge service has not been activated. Are you sure you want to leave this page?';
      return e.returnValue;
    };

    window.addEventListener('beforeunload', preventRefresh);

    return () => {
      unBlock.current();
      window.removeEventListener('beforeunload', preventRefresh);
      if (showWarningRef) showWarningRef.current = false;
    };
  }, [history, isModalOpen]);

  return (
    <ManageListingsContext.Provider
      value={{
        // states
        searchResults,
        loading,
        queryParameter,
        totalCount,
        enabledCount,
        // update states
        setSearchResults,
        setLoading,
        setQueryParameter,
        setEnabledListingsCount,
        // refs
        containerRef,
        footerRef,
        // functions
        openModal,
        sort,
      }}
    >
      {children}
    </ManageListingsContext.Provider>
  );
};

export default ManageListingsContext;
